import { LogoutButton } from '@/components/buttons';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { CustomContentSection } from '@/components/custom-content-section';
import { useLayoutContext } from '@/contexts';
import { useCurrentUser } from '@/hooks';
import { ContentWithLoad } from '@/layouts/content-with-load';
import { PrimaryLayout } from '@/layouts/primary';
import { Affix, Flex, Transition } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../../backend/src/types.generated';
import { AccountForm, AccountFormValues } from './form';
import {
	useGetFacilityUserAccountQuery,
	useUpdateFacilityUserAccountMutation,
} from './graphql.generated';

export const FacilitySettingsPage = () => {
	const navigate = useNavigate();
	const { currentUser, loading: userLoading, refetchUser, logUserOut } = useCurrentUser();
	const [shouldSubmit, setShouldSubmit] = useState(false);
	const {
		data,
		loading,
		error,
		refetch: refetchUserAccount,
	} = useGetFacilityUserAccountQuery({
		variables: { id: currentUser?.id || '' },
		skip: !currentUser?.id,
	});
	const [updateUserAccount, { loading: updatingUser }] = useUpdateFacilityUserAccountMutation();
	const { isDesktop } = useLayoutContext();

	const formRef = useRef<FormikProps<AccountFormValues>>(null);
	const submitForm = useCallback(() => {
		formRef.current?.submitForm();
	}, []);

	const saveDetailsButton = useMemo(
		() => (
			<StandardButton loading={updatingUser} disabled={!shouldSubmit} onClick={submitForm}>
				Save Details
			</StandardButton>
		),
		[submitForm, updatingUser, shouldSubmit]
	);

	const cancelButton = useMemo(
		() => (
			<StandardButton variant="default" onClick={() => formRef.current?.resetForm()}>
				Cancel
			</StandardButton>
		),
		[formRef]
	);

	const logoutButton = useMemo(() => <LogoutButton />, []);

	const handleSubmit = useCallback(
		async (values: AccountFormValues) => {
			try {
				if (!currentUser?.id) {
					throw new Error('User is not authenticated');
				}

				await updateUserAccount({
					variables: {
						input: {
							email: values.email.toLowerCase(),
							firstName: values.firstName,
							lastName: values.lastName,
						},
					},
				});

				if (currentUser?.contact?.email?.toLowerCase() !== values.email.toLowerCase()) {
					await logUserOut();
					navigate({ to: '/welcome' });
				} else {
					await refetchUser();
					await refetchUserAccount();
					navigate({ to: '/facility/home' });
				}
			} catch (error) {
				// TODO: need some error handling to give the user feedback
				console.error(error);
			}
		},
		[
			currentUser?.id,
			currentUser?.contact?.email,
			updateUserAccount,
			logUserOut,
			navigate,
			refetchUser,
			refetchUserAccount,
		]
	);

	const handleChangePassword = useCallback(
		() => navigate({ to: '/facility/change-password' }),
		[navigate]
	);

	const handleDeleteAccount = useCallback(
		() => navigate({ to: '/facility/delete-account' }),
		[navigate]
	);

	const initialValues: AccountFormValues = useMemo(() => {
		return {
			firstName: data?.user?.contact?.firstName || '',
			lastName: data?.user?.contact?.lastName || '',
			email: data?.user?.contact?.email?.toLowerCase() || '',
		};
	}, [data?.user]);

	return (
		<PrimaryLayout
			header={{ title: 'Settings', children: logoutButton }}
			footer={{ children: saveDetailsButton }}
			context="facility"
		>
			<ContentWithLoad
				loading={loading || userLoading}
				error={!!error}
				isEmpty={!data?.user}
				isEmptyMessage="Please log in or refresh to view your account"
				isErrorMessage="An error occurred while loading your account"
			>
				<ContentPanel hasTopNav width="md" gap="md">
					<CustomContentSection
						location={ContentSectionLocation.Account}
						placement={ContentSectionPlacement.Top}
					/>
					<AccountForm
						formRef={formRef}
						setShouldSubmit={setShouldSubmit}
						onSubmit={handleSubmit}
						initialValues={initialValues}
					/>
					<CustomContentSection
						location={ContentSectionLocation.Account}
						placement={ContentSectionPlacement.Middle}
					/>
					{isDesktop ? (
						<Flex gap="lg" pt="sm" w="100%" justify="center">
							<StandardButton variant="transparent" onClick={handleChangePassword}>
								Change Password
							</StandardButton>
							<StandardButton variant="danger" onClick={handleDeleteAccount}>
								Delete Account
							</StandardButton>
						</Flex>
					) : (
						<>
							<StandardButton variant="transparent" onClick={handleChangePassword}>
								Change Password
							</StandardButton>
							<StandardButton variant="danger" onClick={handleDeleteAccount}>
								Delete Account
							</StandardButton>
						</>
					)}
					<CustomContentSection
						location={ContentSectionLocation.Account}
						placement={ContentSectionPlacement.Bottom}
					/>
				</ContentPanel>
				<Transition
					mounted={shouldSubmit && isDesktop}
					transition="slide-up"
					duration={400}
					timingFunction="ease"
				>
					{(styles) => (
						<Affix style={styles} w="100%" position={{ bottom: 0, left: 0 }}>
							<Flex w="screen" py="sm" gap="lg" bg="white" justify="center" px="xl">
								<Flex w={300}>{cancelButton}</Flex>
								<Flex w={300}>{saveDetailsButton}</Flex>
							</Flex>
						</Affix>
					)}
				</Transition>
			</ContentWithLoad>
		</PrimaryLayout>
	);
};
